import { useCallback, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';

// Hook for theme transitions and animations
export const useThemeTransitions = () => {
  const { resolvedTheme } = useTheme();

  const transitionClasses = useMemo(() => ({
    base: 'transition-all duration-300 ease-in-out',
    colors: 'transition-colors duration-300 ease-in-out',
    background: 'transition-colors duration-300 ease-in-out',
    border: 'transition-colors duration-300 ease-in-out',
    shadow: 'transition-shadow duration-300 ease-in-out',
    transform: 'transition-transform duration-200 ease-in-out',
    opacity: 'transition-opacity duration-200 ease-in-out',
  }), []);

  const animationClasses = useMemo(() => ({
    fadeIn: 'animate-fade-in',
    slideIn: 'animate-slide-in-up',
    scaleIn: 'animate-scale-in',
    pulse: 'animate-pulse-slow',
  }), []);

  return {
    resolvedTheme,
    transitionClasses,
    animationClasses,
  };
};

// Hook for theme-aware styling utilities
export const useThemeUtils = () => {
  const { resolvedTheme, colors, isDark, isLight } = useTheme();

  // Generate theme-aware class names
  const getThemeClass = useCallback(
    (lightClass: string, darkClass: string) => {
      return isDark ? darkClass : lightClass;
    },
    [isDark]
  );

  // Generate conditional classes based on theme
  const themeClass = useCallback(
    (classes: { light?: string; dark?: string; base?: string }) => {
      const baseClasses = classes.base || '';
      const themeSpecific = isDark ? classes.dark || '' : classes.light || '';
      return `${baseClasses} ${themeSpecific}`.trim();
    },
    [isDark]
  );

  // Get CSS custom property value
  const getCSSVar = useCallback((property: string) => {
    return `var(--theme-${property})`;
  }, []);

  // Generate inline styles from theme colors
  const getThemeStyles = useCallback(
    (styleMap: Record<string, string>) => {
      const styles: Record<string, string> = {};
      
      Object.entries(styleMap).forEach(([prop, colorPath]) => {
        // Check if it's a theme color path
        const colorValue = getNestedValue(colors, colorPath);
        if (colorValue) {
          styles[prop] = colorValue;
        } else {
          // Use as direct value
          styles[prop] = colorPath;
        }
      });
      
      return styles;
    },
    [colors]
  );

  // Theme-aware background patterns
  const backgroundPatterns = useMemo(() => ({
    primary: themeClass({
      light: 'bg-white',
      dark: 'bg-navy-950',
    }),
    secondary: themeClass({
      light: 'bg-slate-50',
      dark: 'bg-navy-900',
    }),
    elevated: themeClass({
      light: 'bg-white shadow-sm',
      dark: 'bg-navy-800 shadow-lg',
    }),
    card: themeClass({
      light: 'bg-white border border-slate-200 shadow-sm',
      dark: 'bg-navy-800 border border-navy-600 shadow-lg',
    }),
    interactive: themeClass({
      light: 'bg-white hover:bg-slate-50 border border-slate-200',
      dark: 'bg-navy-800 hover:bg-navy-700 border border-navy-600',
    }),
  }), [themeClass]);

  // Theme-aware text patterns
  const textPatterns = useMemo(() => ({
    primary: themeClass({
      light: 'text-slate-900',
      dark: 'text-white',
    }),
    secondary: themeClass({
      light: 'text-slate-600',
      dark: 'text-slate-300',
    }),
    tertiary: themeClass({
      light: 'text-slate-500',
      dark: 'text-slate-400',
    }),
    inverse: themeClass({
      light: 'text-white',
      dark: 'text-slate-900',
    }),
    accent: themeClass({
      light: 'text-professional-blue-600',
      dark: 'text-professional-blue-400',
    }),
  }), [themeClass]);

  // Theme-aware border patterns
  const borderPatterns = useMemo(() => ({
    primary: themeClass({
      light: 'border-slate-200',
      dark: 'border-navy-600',
    }),
    secondary: themeClass({
      light: 'border-slate-300',
      dark: 'border-navy-500',
    }),
    focus: themeClass({
      light: 'border-professional-blue-500 ring-2 ring-professional-blue-200',
      dark: 'border-professional-blue-400 ring-2 ring-professional-blue-900',
    }),
    interactive: themeClass({
      light: 'border-slate-200 hover:border-slate-300',
      dark: 'border-navy-600 hover:border-navy-500',
    }),
  }), [themeClass]);

  // Status color utilities
  const statusColors = useMemo(() => ({
    success: {
      background: themeClass({
        light: 'bg-green-50 border-green-200',
        dark: 'bg-green-900/20 border-green-700',
      }),
      text: themeClass({
        light: 'text-green-800',
        dark: 'text-green-300',
      }),
      icon: colors.status.success,
    },
    warning: {
      background: themeClass({
        light: 'bg-yellow-50 border-yellow-200',
        dark: 'bg-yellow-900/20 border-yellow-700',
      }),
      text: themeClass({
        light: 'text-yellow-800',
        dark: 'text-yellow-300',
      }),
      icon: colors.status.warning,
    },
    error: {
      background: themeClass({
        light: 'bg-red-50 border-red-200',
        dark: 'bg-red-900/20 border-red-700',
      }),
      text: themeClass({
        light: 'text-red-800',
        dark: 'text-red-300',
      }),
      icon: colors.status.error,
    },
    info: {
      background: themeClass({
        light: 'bg-blue-50 border-blue-200',
        dark: 'bg-blue-900/20 border-blue-700',
      }),
      text: themeClass({
        light: 'text-blue-800',
        dark: 'text-blue-300',
      }),
      icon: colors.status.info,
    },
  }), [themeClass, colors.status]);

  // Risk level utilities
  const riskColors = useMemo(() => ({
    low: {
      background: themeClass({
        light: 'bg-emerald-50 border-emerald-200',
        dark: 'bg-emerald-900/20 border-emerald-700',
      }),
      text: themeClass({
        light: 'text-emerald-800',
        dark: 'text-emerald-300',
      }),
      icon: colors.risk.low,
    },
    medium: {
      background: themeClass({
        light: 'bg-amber-50 border-amber-200',
        dark: 'bg-amber-900/20 border-amber-700',
      }),
      text: themeClass({
        light: 'text-amber-800',
        dark: 'text-amber-300',
      }),
      icon: colors.risk.medium,
    },
    high: {
      background: themeClass({
        light: 'bg-red-50 border-red-200',
        dark: 'bg-red-900/20 border-red-700',
      }),
      text: themeClass({
        light: 'text-red-800',
        dark: 'text-red-300',
      }),
      icon: colors.risk.high,
    },
    critical: {
      background: themeClass({
        light: 'bg-red-100 border-red-300',
        dark: 'bg-red-900/30 border-red-600',
      }),
      text: themeClass({
        light: 'text-red-900',
        dark: 'text-red-200',
      }),
      icon: colors.risk.critical,
    },
  }), [themeClass, colors.risk]);

  return {
    resolvedTheme,
    colors,
    isDark,
    isLight,
    getThemeClass,
    themeClass,
    getCSSVar,
    getThemeStyles,
    backgroundPatterns,
    textPatterns,
    borderPatterns,
    statusColors,
    riskColors,
  };
};

// Utility function to get nested object values
function getNestedValue(obj: any, path: string): string | undefined {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

// Hook for responsive theme utilities
export const useResponsiveTheme = () => {
  const themeUtils = useThemeUtils();
  
  const responsive = useCallback((
    config: {
      base?: string;
      sm?: string;
      md?: string;
      lg?: string;
      xl?: string;
    }
  ) => {
    const classes = [
      config.base || '',
      config.sm ? `sm:${config.sm}` : '',
      config.md ? `md:${config.md}` : '',
      config.lg ? `lg:${config.lg}` : '',
      config.xl ? `xl:${config.xl}` : '',
    ].filter(Boolean);
    
    return classes.join(' ');
  }, []);

  return {
    ...themeUtils,
    responsive,
  };
};